/**
 * YUMIX Dynamic Branding — Theme Manager
 * Changes CSS variables based on active cultural/clinical mode for total immersion.
 */

// Default theme (YUMIX orange/dark)
const DEFAULT_THEME = {
  '--primary': '15 90% 55%',
  '--primary-foreground': '0 0% 100%',
  '--accent': '35 100% 50%',
  '--accent-foreground': '0 0% 0%',
  '--ring': '15 90% 55%',
  '--border': '0 0% 20%',
  '--card': '0 0% 11%',
};

const MODE_THEMES = {
  // ==================== CULTURAL MODES ====================
  
  ramadan: {
    '--primary': '45 80% 45%',           // Warm gold
    '--primary-foreground': '0 0% 100%',
    '--accent': '35 90% 50%',            // Deep amber
    '--accent-foreground': '0 0% 0%',
    '--ring': '45 80% 45%',
    '--border': '45 30% 18%',            // Gold-tinted border
    '--card': '40 15% 10%',              // Warm dark card
  },

  halal: {
    '--primary': '160 65% 42%',          // Emerald green
    '--primary-foreground': '0 0% 100%',
    '--accent': '145 55% 45%',           // Soft green
    '--accent-foreground': '0 0% 0%',
    '--ring': '160 65% 42%',
    '--border': '160 20% 18%',
    '--card': '160 10% 10%',
  },

  kasher: {
    '--primary': '220 70% 55%',          // Royal blue
    '--primary-foreground': '0 0% 100%',
    '--accent': '210 60% 50%',           // Deep blue
    '--accent-foreground': '0 0% 100%',
    '--ring': '220 70% 55%',
    '--border': '220 20% 20%',
    '--card': '220 15% 11%',
  },

  careme: {
    '--primary': '280 50% 55%',          // Royal purple
    '--primary-foreground': '0 0% 100%',
    '--accent': '270 45% 50%',           // Soft purple
    '--accent-foreground': '0 0% 100%',
    '--ring': '280 50% 55%',
    '--border': '280 15% 20%',
    '--card': '275 12% 11%',
  },

  hindou: {
    '--primary': '25 90% 55%',           // Saffron orange
    '--primary-foreground': '0 0% 100%',
    '--accent': '15 85% 50%',            // Deep saffron
    '--accent-foreground': '0 0% 100%',
    '--ring': '25 90% 55%',
    '--border': '25 25% 18%',
    '--card': '20 15% 10%',
  },

  // ==================== CLINICAL MODES ====================

  neuro_support: {
    '--primary': '240 55% 55%',          // Calm indigo
    '--primary-foreground': '0 0% 100%',
    '--accent': '250 50% 60%',           // Soft lavender
    '--accent-foreground': '0 0% 100%',
    '--ring': '240 55% 55%',
    '--border': '240 15% 20%',
    '--card': '240 15% 10%',
  },

  teen_growth: {
    // Legacy alias kept for backward compat with existing user docs.
    // Maps to a neutral neuro-style theme. Not exposed in UI.
    '--primary': '240 55% 55%',
    '--primary-foreground': '0 0% 100%',
    '--accent': '250 50% 60%',
    '--accent-foreground': '0 0% 100%',
    '--ring': '240 55% 55%',
    '--border': '240 15% 20%',
    '--card': '240 15% 10%',
  },

  oncology: {
    '--primary': '340 65% 55%',          // Compassionate rose
    '--primary-foreground': '0 0% 100%',
    '--accent': '350 55% 50%',
    '--accent-foreground': '0 0% 100%',
    '--ring': '340 65% 55%',
    '--border': '340 15% 20%',
    '--card': '340 10% 10%',
  },

  cardiovascular: {
    '--primary': '0 70% 50%',            // Heart red
    '--primary-foreground': '0 0% 100%',
    '--accent': '10 65% 50%',
    '--accent-foreground': '0 0% 100%',
    '--ring': '0 70% 50%',
    '--border': '0 15% 20%',
    '--card': '0 10% 10%',
  },

  bariatric: {
    '--primary': '175 65% 42%',          // Teal
    '--primary-foreground': '0 0% 100%',
    '--accent': '180 55% 45%',
    '--accent-foreground': '0 0% 0%',
    '--ring': '175 65% 42%',
    '--border': '175 15% 18%',
    '--card': '175 10% 10%',
  },

  // ==================== PROFILE MODES ====================

  pilote: {
    '--primary': '0 72% 50%',             // Combat red
    '--primary-foreground': '0 0% 100%',
    '--accent': '0 0% 45%',               // Steel grey
    '--accent-foreground': '0 0% 100%',
    '--ring': '0 72% 50%',
    '--border': '0 15% 22%',
    '--card': '0 5% 11%',
  },

  deesse: {
    '--primary': '43 90% 55%',            // Royal gold
    '--primary-foreground': '0 0% 10%',
    '--accent': '340 65% 60%',            // Rose
    '--accent-foreground': '0 0% 100%',
    '--ring': '43 90% 55%',
    '--border': '43 30% 22%',
    '--card': '340 8% 11%',
  },

  sagesse: {
    '--primary': '230 65% 55%',           // Midnight blue
    '--primary-foreground': '0 0% 100%',
    '--accent': '220 55% 45%',            // Deep navy
    '--accent-foreground': '0 0% 100%',
    '--ring': '230 65% 55%',
    '--border': '230 20% 22%',
    '--card': '230 15% 11%',
  },

  espoir: {
    '--primary': '145 72% 45%',           // Energy green
    '--primary-foreground': '0 0% 100%',
    '--accent': '160 60% 42%',            // Teal growth
    '--accent-foreground': '0 0% 100%',
    '--ring': '145 72% 45%',
    '--border': '145 20% 20%',
    '--card': '145 10% 10%',
  },
};

/**
 * Apply a theme to the document root.
 * Priority: clinical > profile > cultural > default
 */
export function applyTheme(culturalMode, clinicalProfile, activeProfile) {
  const root = document.documentElement;

  // Priority chain: clinical > profile > cultural > default
  let theme = DEFAULT_THEME;
  const activeMode = clinicalProfile || activeProfile || culturalMode;

  if (activeMode && activeMode !== 'standard' && MODE_THEMES[activeMode]) {
    theme = { ...DEFAULT_THEME, ...MODE_THEMES[activeMode] };
  }

  // Apply CSS variables
  Object.entries(theme).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
}

/**
 * Profile definitions with labels, icons, colors, and messages
 */
export const PROFILE_DEFINITIONS = {
  pilote: {
    id: 'pilote',
    label: 'Pilote',
    icon: 'Target',
    emoji: null,
    colors: { primary: '#e03e3e', accent: '#737373', gradient: 'from-red-600 to-gray-600' },
    message_fr: 'Performance engagee. Prêt pour l\'assaut, Pilote.',
    message_en: 'Performance engaged. Ready for the assault, Pilot.',
    description_fr: 'Priorité aux protéines. Mode performance et construction musculaire.',
    description_en: 'Protein priority. Performance and muscle building mode.',
  },
  deesse: {
    id: 'deesse',
    label: 'Deesse',
    icon: 'Crown',
    emoji: null,
    colors: { primary: '#d4a017', accent: '#d4668e', gradient: 'from-yellow-500 to-pink-500' },
    message_fr: 'Souveraineté activée. Bienvenue, Déesse.',
    message_en: 'Sovereignty activated. Welcome, Goddess.',
    description_fr: 'Support cycle hormonal. +250 kcal en phase lutéale, fer et magnésium.',
    description_en: 'Hormonal cycle support. +250 kcal in luteal phase, iron & magnesium.',
  },
  sagesse: {
    id: 'sagesse',
    label: 'Sagesse',
    icon: 'Shield',
    emoji: null,
    colors: { primary: '#4a6cf7', accent: '#3451b2', gradient: 'from-blue-600 to-blue-900' },
    message_fr: 'Vitalite prioritaire. Bienvenue dans ton cercle Sagesse.',
    message_en: 'Vitality prioritized. Welcome to your Wisdom circle.',
    description_fr: 'Densité nutritionnelle et longévité. Focus fibres et micronutriments.',
    description_en: 'Nutritional density and longevity. Fiber and micronutrient focus.',
  },
  espoir: {
    id: 'espoir',
    label: 'Espoir',
    icon: 'Flame',
    emoji: null,
    colors: { primary: '#22c55e', accent: '#14b8a6', gradient: 'from-green-500 to-teal-500' },
    message_fr: 'Potentiel debloque. En route pour l\'évolution, Espoir.',
    message_en: 'Potential unlocked. On your way to evolution, Hope.',
    description_fr: 'Zéro déficit calorique. Focus croissance et protéines.',
    description_en: 'Zero calorie deficit. Growth and protein focus.',
  },
  manual_coach: {
    id: 'manual_coach',
    label: 'Mode Coach',
    icon: 'Sliders',
    emoji: null,
    colors: { primary: '#f97316', accent: '#ea580c', gradient: 'from-orange-500 to-orange-700' },
    message_fr: 'Mode Expert active. Le Coach decide.',
    message_en: 'Expert Mode active. The Coach decides.',
    description_fr: 'Macros manuelles. Bypass Mifflin-St Jeor. Contrôle total.',
    description_en: 'Manual macros. Bypass Mifflin-St Jeor. Full control.',
  },
};

/**
 * Reset to default YUMIX theme
 */
export function resetTheme() {
  applyTheme(null, null);
}

/**
 * Get the mode label and icon for display
 */
export function getActiveModeInfo(culturalMode, clinicalProfile) {
  if (clinicalProfile && clinicalProfile !== 'standard' && MODE_THEMES[clinicalProfile]) {
    const labels = {
      neuro_support: { label: 'Neuro-Soutien', icon: '🧠' },
      oncology: { label: 'Oncologie', icon: '🎗️' },
      cardiovascular: { label: 'Cardiovasculaire', icon: '❤️' },
      bariatric: { label: 'Bariatrique', icon: '⚖️' },
    };
    return labels[clinicalProfile] || null;
  }
  if (culturalMode && culturalMode !== 'standard' && MODE_THEMES[culturalMode]) {
    const labels = {
      ramadan: { label: 'Ramadan', icon: '🌙' },
      halal: { label: 'Halal', icon: '☪️' },
      kasher: { label: 'Kasher', icon: '✡️' },
      careme: { label: 'Carême', icon: '✝️' },
      hindou: { label: 'Hindou', icon: '🕉️' },
    };
    return labels[culturalMode] || null;
  }
  return null;
}
